/**
 * The card's motion (spec "Frontend" → Motion): the dock slides up in 0.3 s on
 * the sheet ease, questions cross-fade and slide in 0.22 s, and under reduced
 * motion everything only fades. House tokens only (`@/lib/motion`); transform
 * and opacity only.
 */
import type { MotionProps, Variants } from "framer-motion";

import { MOTION } from "@/lib/motion";

/** How far the docked card rises into place. */
const DOCK_RISE_PX = 24;
/** How far a question slides while it cross-fades. */
const QUESTION_SHIFT_PX = 16;
const QUESTION_TRANSITION = { duration: MOTION.dur.bubble, ease: MOTION.ease.out };

export function cardMotion(reduced: boolean): Pick<MotionProps, "initial" | "animate" | "transition"> {
  return reduced
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { duration: MOTION.dur.nav, ease: MOTION.ease.out },
      }
    : {
        initial: { opacity: 0, y: DOCK_RISE_PX },
        animate: { opacity: 1, y: 0 },
        transition: { duration: MOTION.dur.slideOver, ease: MOTION.ease.standard },
      };
}

/** Where the incoming question starts: +1 = moving forward, -1 = back. */
export function questionEnter(direction: number, reduced: boolean): { opacity: number; x: number } {
  return { opacity: 0, x: reduced ? 0 : direction * QUESTION_SHIFT_PX };
}

/** Where the outgoing question goes: away from the incoming one. */
export function questionExit(
  direction: number,
  reduced: boolean,
): { opacity: number; x: number; transition: { duration: number; ease: "easeOut" } } {
  return { opacity: 0, x: reduced ? 0 : -direction * QUESTION_SHIFT_PX, transition: QUESTION_TRANSITION };
}

export function questionVariants(reduced: boolean): Variants {
  return {
    enter: (direction: number) => questionEnter(direction, reduced),
    center: { opacity: 1, x: 0, transition: QUESTION_TRANSITION },
    exit: (direction: number) => questionExit(direction, reduced),
  };
}
